/* global document */
import counterIncrement from '../utils/counter-increment';

/**
 * Handles the number of items to show and enables the loading of more items
 *
 * @param {htmlNode} rootElement The component root node
 * @param {Object} config The component configuration
 */
export default function(rootElement, config) {
  if (!rootElement || !config) {
    return false;
  }

  const children = getChildItems(rootElement);

  if (config.itemsToShow >= children.length) {
    return false;
  }

  const loadMoreButton = createLoadMoreButton(rootElement, config);
  loadMoreButton.addEventListener('click', (e) => {
    e.preventDefault();
    const visibleItems = parseInt(rootElement.getAttribute('data-items-to-show'), 10);
    const itemsToIncrement = getIncrement(rootElement, config);
    const itemsToShow = visibleItems + itemsToIncrement;
    setItemsToShow(rootElement, config, itemsToShow);
  });

  return activate(rootElement, config);
}

/**
 * Applies the data-active attribute to initialise the component and disable the default css
 *
 * @param {htmlNode} rootElement The component root node
 */
function activate(rootElement, config) {
  setInitialItemsToShow(rootElement, config);
  rootElement.setAttribute('data-active', true);
}

/**
 * Sets the initial number of items to show if specified by the child counter-increment
 *
 * @param {htmlNode} rootElement The component root node
 * @param {Object} config The component configuration
 */
function setInitialItemsToShow(rootElement, config) {
  setItemsToShow(rootElement, config, config.itemsToShow);
}

/**
 * Sets the number of items to show and hides the load more button where appropriate
 *
 * @param {htmlNode} rootElement The component root node
 * @param {Object} config The component configuration
 * @param {Integer} itemsToShow The number of items to show
 */
function setItemsToShow(rootElement, config, itemsToShow) {
  const children = getChildItems(rootElement);
  rootElement.setAttribute('data-items-to-show', itemsToShow);
  showItems(children, itemsToShow);

  if (itemsToShow >= children.length) {
    hideLoadMoreButton(rootElement);
  }
}

/**
 * Shows/hides the items within the component
 *
 * @param {Object} items The items to show/hide
 * @param {Integer} itemsToShow The number of items to show
 */
function showItems(items, itemsToShow) {
  for (let i = 0; i < items.length; i += 1) {
    if (i >= itemsToShow) {
      hideItem(items[i]);
    } else {
      showItem(items[i]);
    }
  }
}

/**
 * Hides an item
 *
 * @param {htmlNode} item The item to hide
 */
function hideItem(item) {
  item.setAttribute('aria-hidden', true);
}

/**
 * Shows an item
 *
 * @param {htmlNode} item The item to show
 */
function showItem(item) {
  item.removeAttribute('aria-hidden');
}

/**
 * Gets the number of items to load
 *
 * @param {htmlNode} rootElement The component root node
 * @param {Object} config The component configuration
 * @return {Integer}
 */
function getIncrement(rootElement, config) {
  const childIncrement = getChildItemsToShow(rootElement);
  const increment = childIncrement || config.itemsToIncrement;

  return increment;
}

/**
 * Gets the number of items to load if specified by the child counter-increment
 *
 * @param {htmlNode} rootElement The component root node
 * @return {Integer|Boolean}
 */
function getChildItemsToShow(rootElement) {
  const child = getFirstChild(rootElement);
  const itemsToShow = child ? counterIncrement(child, 'items-to-show-') : false;
  return itemsToShow;
}

/**
 * Gets the first child of the component
 *
 * @param {htmlNode} rootElement The component root node
 * @return {htmlNode}
 */
function getFirstChild(element) {
  return element.querySelector('[data-role="load-more-item"]');
}

/**
 * Gets the elements used by the component
 *
 * @param {htmlNode} rootElement The component root node
 * @return {htmlNode}
 */
function getChildItems(element) {
  return element.querySelectorAll('[data-role="load-more-item"]');
}

/**
 * Creates the load more button and appends it to the component
 *
 * @param {htmlNode} rootElement The component root node
 * @param {Object} config The component configuration
 * @return {htmlNode}
 */
function createLoadMoreButton(rootElement, config) {
  const button = document.createElement(config.href ? 'a' : 'button');
  button.setAttribute('data-role', 'load-more-button');
  button.className = 'sdc-site-load-more__button';
  button.innerText = config.loadMoreButtonText;
  config.href ? button.setAttribute('href', config.href) : false;

  rootElement.appendChild(button);

  return button;
}

/**
 * Hidess the load more button
 *
 * @param {htmlNode} rootElement The component root node
 */
function hideLoadMoreButton(rootElement) {
  const button = rootElement.querySelector('.sdc-site-load-more__button');
  button.className = 'sdc-site-load-more__button--is-hidden';
}
